import React from "react";
import { FiFileText, FiGift, FiImage, FiPackage, FiTag } from "react-icons/fi";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./AdminStoreCategories.css";

const slugify = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .trim();

const DEFAULT_FORM = {
  name: "",
  slug: "",
  description: "",
  icon: "tag",
  slugTouched: false,
};

const iconOptions = [
  { value: "tag", label: "Etiqueta" },
  { value: "package", label: "Caixa / Produto" },
  { value: "file", label: "Papelaria" },
  { value: "gift", label: "Brinde" },
  { value: "image", label: "Banner / Imagem" },
];

const iconMap = {
  tag: FiTag,
  package: FiPackage,
  file: FiFileText,
  gift: FiGift,
  image: FiImage,
};

const AdminStoreCategories = () => {
  const [menuClose, setMenuClose] = React.useState(false);
  const [categories, setCategories] = React.useState([]);
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const toggleMenu = () => setMenuClose((prev) => !prev);

  const loadCategories = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await Api.get("/admin/store-categories");
      setCategories(Array.isArray(response?.data?.items) ? response.data.items : []);
    } catch (err) {
      console.error("Erro ao carregar categorias da store:", err);
      setError("Nao foi possivel carregar as categorias da Store.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((current) => {
      if (field === "name") {
        return {
          ...current,
          name: value,
          slug: current.slugTouched ? current.slug : slugify(value),
        };
      }

      if (field === "slug") {
        return {
          ...current,
          slug: slugify(value),
          slugTouched: true,
        };
      }

      return {
        ...current,
        [field]: value,
      };
    });

    setError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("Informe o nome da categoria.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const response = await Api.post("/admin/store-categories", {
        name: form.name,
        slug: form.slug,
        description: form.description,
        icon: form.icon,
      });

      const created = response?.data?.item;
      if (created) {
        setCategories((current) => [...current, created].sort((a, b) => a.position - b.position));
      }

      setForm(DEFAULT_FORM);
      setSuccess("Categoria criada com sucesso. Ela ja aparece no menu da Store.");
    } catch (err) {
      console.error("Erro ao criar categoria da store:", err);
      setError(err?.response?.data?.message || "Nao foi possivel criar a categoria.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("Deseja remover esta categoria da Store?")) return;

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");
      await Api.delete(`/admin/store-categories/${categoryId}`);
      setCategories((current) => current.filter((item) => item.id !== categoryId));
      setSuccess("Categoria removida com sucesso.");
    } catch (err) {
      console.error("Erro ao remover categoria da store:", err);
      setError(err?.response?.data?.message || "Nao foi possivel remover a categoria.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} storeCategoriesActive="active" />

      <main className="main-content">
        <Topbar page="Store / Categorias" onToggleMenu={toggleMenu} />

        <section className="admin-store-categories-grid">
          <article className="admin-store-categories-panel">
            <div className="admin-store-categories-header">
              <div>
                <h2>Criar categoria</h2>
                <p>
                  Cadastre as categorias usadas pelos produtos da Store. Essas categorias aparecem
                  no menu do licenciado.
                </p>
              </div>
              <span className="admin-store-categories-badge">Super administrador</span>
            </div>

            <form className="admin-store-categories-form" onSubmit={handleSubmit}>
              <label className="admin-store-categories-field">
                <span>Nome</span>
                <input value={form.name} onChange={handleChange("name")} placeholder="Ex.: Vestuario" />
              </label>

              <label className="admin-store-categories-field">
                <span>Slug</span>
                <input value={form.slug} onChange={handleChange("slug")} placeholder="Ex.: vestuario" />
              </label>

              <label className="admin-store-categories-field">
                <span>Icone</span>
                <select value={form.icon} onChange={handleChange("icon")}>
                  {iconOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="admin-store-categories-field admin-store-categories-field-full">
                <span>Descricao</span>
                <input
                  value={form.description}
                  onChange={handleChange("description")}
                  placeholder="Texto curto para identificar a categoria"
                />
              </label>

              <div className="admin-store-categories-actions">
                <button type="submit" disabled={submitting}>
                  {submitting ? "Salvando..." : "Criar categoria"}
                </button>
              </div>

              {error ? <div className="admin-store-categories-message error">{error}</div> : null}
              {success ? <div className="admin-store-categories-message success">{success}</div> : null}
            </form>
          </article>

          <article className="admin-store-categories-panel">
            <div className="admin-store-categories-header compact">
              <div>
                <h2>Categorias cadastradas</h2>
                <p>{loading ? "Carregando..." : `${categories.length} categoria(s) ativa(s)`}</p>
              </div>
            </div>

            {loading ? (
              <div className="admin-store-categories-empty">Carregando categorias...</div>
            ) : categories.length === 0 ? (
              <div className="admin-store-categories-empty">Nenhuma categoria cadastrada.</div>
            ) : (
              <div className="admin-store-categories-list">
                {categories.map((category, index) => {
                  const CategoryIcon = iconMap[category.icon] || FiTag;

                  return (
                  <article className="admin-store-category-card" key={category.id}>
                    <div className="admin-store-category-main">
                      <div className="admin-store-category-icon">
                        <CategoryIcon />
                      </div>
                      <div className="admin-store-category-meta">
                        <strong>{category.name}</strong>
                        <span>Slug: {category.slug}</span>
                        <span>{category.description || "Sem descricao"}</span>
                      </div>
                      <div className="admin-store-category-position">#{index + 1}</div>
                    </div>

                    <button
                      type="button"
                      className="admin-store-category-remove"
                      onClick={() => handleDelete(category.id)}
                      disabled={submitting}
                    >
                      Remover
                    </button>
                  </article>
                  );
                })}
              </div>
            )}
          </article>
        </section>
      </main>
    </div>
  );
};

export default AdminStoreCategories;
